/**
 * Выбор языка игры. EN — на корне, RU — на `/ru`, UK — на `/uk` (MADR 0008).
 *
 * Порядок:
 * 1. путь `/ru`, `/uk` и вложенные — этот язык, без вариантов (ссылка на него — явный выбор);
 * 2. Mini App: `language_code` пользователя Telegram (Mini App всегда открывается с корня, путь ничего не значит);
 * 3. браузер: сохранённый выбор игрока, затем `Accept-Language` (`navigator.languages`);
 * 4. иначе английский.
 * После входа язык, который игрок выбрал сам (`localeChosen`), важнее пунктов 2 и 3: `start.ts`.
 */
import { localeForLanguage, type Locale } from "@newfungame/contracts";

export const LOCALES: readonly Locale[] = ["en", "ru", "uk"];
export const DEFAULT_LOCALE: Locale = "en";

export type LocaleSource = "path" | "telegram" | "saved" | "accept-language" | "default";

export interface LocaleInput {
  pathname: string;
  /** `initDataUnsafe.user.language_code` (только в Mini App). */
  telegramLanguage?: string | null;
  /** Открыто как Telegram Mini App. */
  miniApp?: boolean;
  /** Сохранённый выбор игрока (localStorage). */
  saved?: string | null;
  /** `navigator.languages` по убыванию предпочтения. */
  acceptLanguages?: readonly string[];
}

/** Названия языков в переключателе: каждый на своём языке. */
export const LOCALE_NAMES: Record<Locale, string> = { en: "English", ru: "Русский", uk: "Українська" };

/** Локаль чисел для `toLocaleString`. */
export const NUMBER_LOCALES: Record<Locale, string> = { en: "en-US", ru: "ru-RU", uk: "uk-UA" };

/** Язык из пути: `ru`, `uk` для `/ru`, `/uk` и вложенных, иначе `null` (корень — EN, но это не явный выбор). */
export function localeFromPath(pathname: string): Locale | null {
  const m = /^\/(ru|uk)(\/|$)/i.exec(pathname);
  return m ? (m[1]!.toLowerCase() as Locale) : null;
}

/** Код языка (`ru`, `uk-UA`, `en_US`) → поддерживаемая локаль. Неизвестный — `null`. */
export const matchLanguage = localeForLanguage;

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v);
}

/** Язык и откуда он взят. */
export function detectLocale(input: LocaleInput): { locale: Locale; source: LocaleSource } {
  const fromPath = localeFromPath(input.pathname);
  if (fromPath) return { locale: fromPath, source: "path" };
  if (input.miniApp) {
    const tg = matchLanguage(input.telegramLanguage);
    return tg ? { locale: tg, source: "telegram" } : { locale: DEFAULT_LOCALE, source: "default" };
  }
  if (isLocale(input.saved)) return { locale: input.saved, source: "saved" };
  for (const lang of input.acceptLanguages ?? []) {
    // первый язык, который мы знаем, решает: en-US перед ru → английский
    const m = matchLanguage(lang);
    if (m) return { locale: m, source: "accept-language" };
  }
  return { locale: DEFAULT_LOCALE, source: "default" };
}

/**
 * Путь страницы для языка: `/`, `/ru`, `/uk`. Нужен браузеру, чтобы адрес совпадал с языком (поделиться ссылкой, обновить
 * страницу). В Mini App путь не меняем.
 */
export function pathForLocale(pathname: string, locale: Locale): string {
  const rest = pathname.replace(/^\/(ru|uk)(?=\/|$)/i, "") || "/";
  if (locale === "en") return rest;
  return rest === "/" ? `/${locale}` : `/${locale}${rest}`;
}
